import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SectionLabel from "@/components/SectionLabel";
import { api } from "@/lib/api";
import { Search } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/blog", { params: { q: q || undefined, category: cat !== "all" ? cat : undefined } })
      .then(r => setPosts(r.data))
      .finally(() => setLoading(false));
  }, [q, cat]);

  const categories = ["all", ...Array.from(new Set(posts.map(p => p.category))).filter(Boolean)];

  return (
    <div>
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionLabel>Health Blog</SectionLabel>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-900 max-w-3xl">
            Plain-language health guidance from your family physician.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Articles reviewed by Dr. Onkar before publication. For personal medical advice,
            please book a consultation.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search articles…"
                     className="w-full rounded-full border border-slate-200 pl-9 pr-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0A6EBD]"
                     data-testid="blog-search" />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button key={c} onClick={() => setCat(c)}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${cat === c ? "bg-[#0A6EBD] text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                        data-testid={`blog-cat-${c}`}>
                  {c === "all" ? "All" : c}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="mt-10 text-slate-500">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="mt-10 text-slate-500">No articles match your search.</div>
          ) : (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`}
                      className="group rounded-2xl overflow-hidden border border-slate-200/70 bg-white aarogya-shadow hover:-translate-y-0.5 transition"
                      data-testid={`blog-card-${p.slug}`}>
                  {p.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-teal-600 font-semibold">{p.category}</div>
                    <div className="mt-2 font-serif text-xl text-slate-900 leading-snug group-hover:text-[#0A6EBD]">{p.title}</div>
                    <div className="mt-2 text-sm text-slate-600 line-clamp-3">{p.excerpt}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
